import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  FormControl,
  OutlinedInput,
  InputLabel,
  Link,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Controller, useForm } from 'react-hook-form';
import validationUtils from '../../services/validationUtils';
import TextInput2 from '../form/TextInput2';
import { useNavigate } from 'react-router-dom';

const PREFIX = 'LoginForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  title: `${PREFIX}-title`,
  password: `${PREFIX}-password`,
  error: `${PREFIX}-error`,
  submit: `${PREFIX}-submit`,
  link: `${PREFIX}-link`,
};

const Root = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    width: 305,
    padding: 32,
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.title}`]: {
    fontSize: 21,
    fontFamily: 'Bitter',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.password}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.error}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.submit}`]: {
    marginTop: theme.spacing(4),
  },

  [`& .${classes.link}`]: {
    display: 'block',
    marginTop: theme.spacing(4),
  },
}));

interface LoginFormProps {
  returnPage?: string;
  error?: string;
  submit: (info: any) => void;
}

export default function LoginForm({
  returnPage,
  error,
  submit,
}: LoginFormProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const requiredFieldMessage = t('common.requiredField');
  const { control, formState, handleSubmit, watch, getValues } = useForm({
    defaultValues: {
      Email: '',
      Password: '',
    } as any,
  });
  const [passwordVisible, setPasswordVisible] = React.useState(false);

  return (
    <Root className={classes.container}>
      <Typography className={classes.title}>{t('login.loginTitle')}</Typography>
      <form onSubmit={handleSubmit(submit)} className={classes.form}>
        <TextInput2
          label={t('login.username')}
          name={'Email'}
          required={true}
          control={control}
          errors={formState.errors}
          rules={{
            required: requiredFieldMessage,
            pattern: validationUtils.emailValidation(),
          }}
        />
        <FormControl variant="outlined" className={classes.password}>
          <InputLabel htmlFor="password" required={true}>
            {t('login.password')}
          </InputLabel>
          <Controller
            name={'Password'}
            control={control}
            defaultValue={''}
            rules={{
              required: requiredFieldMessage,
            }}
            render={({ field }) => (
              <OutlinedInput
                {...field}
                id="password"
                type={passwordVisible ? 'text' : 'password'}
                required={true}
                error={
                  formState.errors ? !!formState.errors['Password'] : false
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      onMouseDown={(evt) => evt.preventDefault()}
                      edge="end">
                      {passwordVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t('login.password')}
              />
            )}
          />
        </FormControl>
        <Typography color="error" className={classes.error}>
          {error}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          className={classes.submit}
          type="submit">
          {t('login.login')}
        </Button>
      </form>
      <Link
        href="#"
        onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          evt.preventDefault();
          navigate('/forgotPassword');
        }}
        color="primary"
        underline="hover"
        align="center"
        className={classes.link}>
        {t('login.forgotPassword')}
      </Link>
      <Link
        href="#"
        onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          evt.preventDefault();
          navigate(`/internalLogin?returnPage=${returnPage}`);
        }}
        className={classes.link}
        color="primary"
        underline="hover"
        align="center">
        {t('login.internalLogin')}
      </Link>
    </Root>
  );
}
